import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { api } from '../../utils/api';
import { moneyTight as baht } from '../../utils/region';
import { useStore } from '../../store';
import { brandOr, firmLabelOr } from '../../utils/brand';

// Financial report — per group company (SLF Accounting / SLF Legal) for a date range: income (corporate /
// personal / total / net-of-pass-through), expenses, invoiced+quoted, receipted. Admin grants who sees it.

type Fig = {
  companyId: string; name: string;
  income: { corporate: number; personal: number; total: number; passThrough: number; net: number };
  expenses: number;
  expensesSplit?: {
    clientTax: number; cash: number; operating: number;
    own?: { salary: number; rent: number; utilities: number; internet: number; other: number };
    ownTotal?: number; unallocated?: number; clientPaid?: number; unclassified?: number;
  };
  invoiced: number; quoted: number; invoicedQuoted: number; receipted: number;
};
type Report = { from: string; to: string; companies: Fig[]; total: Omit<Fig, 'companyId' | 'name'> };

const pad = (n: number) => String(n).padStart(2, '0');
const ymd = (d: Date) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

function presetRange(preset: string): { from: string; to: string } {
  const now = new Date(); const y = now.getFullYear(); const m = now.getMonth();
  if (preset === 'last_month') { const d = new Date(y, m - 1, 1); return { from: ymd(d), to: ymd(new Date(d.getFullYear(), d.getMonth() + 1, 0)) }; }
  if (preset === 'this_year') return { from: `${y}-01-01`, to: `${y}-12-31` };
  return { from: ymd(new Date(y, m, 1)), to: ymd(new Date(y, m + 1, 0)) };   // this_month (default)
}

export default function FinanceReport() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [preset, setPreset] = useState('this_month');
  const init = presetRange('this_month');
  const [from, setFrom] = useState(init.from);
  const [to, setTo] = useState(init.to);
  const [companyId, setCompanyId] = useState('all');
  const { me } = useStore();
  const isAdmin = me?.role === 'admin';
  const [data, setData] = useState<Report | null>(null);
  const [mine, setMine] = useState<{ from: string; to: string; firms: any[] } | null>(null);
  const [mktAll, setMktAll] = useState<{ from: string; to: string; firms: any[] } | null>(null);
  const [tab, setTab] = useState('firm');   // 'firm' = the (admin) firm overview; 'mine-legal' / 'mine-accounting' / 'mkt-all'
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    const [r, m, ma] = await Promise.all([
      api.financeReport(from, to).catch(() => null),   // null when the user lacks the finance_report module
      api.financeMine(from, to).catch(() => null),     // the user's own marketing share (any staff)
      isAdmin ? api.financeMarketingAll(from, to).catch(() => null) : Promise.resolve(null),   // admin: everyone's
    ]);
    setData(r as any); setMine(m as any); setMktAll(ma as any);
    setLoading(false);
  }, [from, to, isAdmin]);
  useEffect(() => { load(); }, [load]);
  // If the user has no firm-overview access, default to their first marketing tab.
  useEffect(() => { if (!data && mine && mine.firms.length && tab === 'firm') setTab('mine-' + mine.firms[0].firm); }, [data, mine]); // eslint-disable-line

  const [classifying, setClassifying] = useState(false);
  // The endpoint deliberately does ONE batch per request (nginx caps a request at 120s), so the
  // caller loops until nothing is left. The guard on `rounds` stops a pathological server answer
  // from spinning here forever.
  const classify = async () => {
    if (classifying) return;
    setClassifying(true);
    try {
      for (let rounds = 0; rounds < 40; rounds++) {
        const r: any = await api.financeClassify(from, to);
        if (r?.done || !(r?.remaining > 0)) break;
      }
      await load();
    } catch (e: any) { alert(e.message); }
    finally { setClassifying(false); }
  };

  const onPreset = (p: string) => { setPreset(p); if (p !== 'custom') { const r = presetRange(p); setFrom(r.from); setTo(r.to); } };

  const shown: Fig[] = useMemo(() => {
    if (!data) return [];
    return companyId === 'all' ? data.companies : data.companies.filter(c => c.companyId === companyId);
  }, [data, companyId]);

  const sel: React.CSSProperties = { padding: '7px 10px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--bg-surface)', color: 'var(--text-primary)', fontSize: 13, fontWeight: 600, cursor: 'pointer' };
  const dateInp: React.CSSProperties = { ...sel, fontWeight: 400, cursor: 'text' };

  return (
    <div style={{ height: '100vh', display: 'flex', flexDirection: 'column', background: 'var(--bg-app)' }}>
      <header style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 16px', borderBottom: '1px solid var(--border)', background: 'var(--bg-surface)' }}>
        <button onClick={() => navigate('/')} title={brandOr('Home')} style={{ width: 30, height: 30, padding: 0, border: 'none', background: 'transparent', cursor: 'pointer' }}>
          <img className="app-logo" src="/logo.png" alt={brandOr('Logo')} style={{ width: 28, height: 28, objectFit: 'contain' }} />
        </button>
        <div style={{ fontSize: 16, fontWeight: 800, color: 'var(--text-primary)' }}>{t('fr_title')}</div>
      </header>

      <div style={{ flex: 1, overflow: 'auto', padding: '16px 20px 60px' }}>
        {/* Tabs: firm overview (admin) + the user's own marketing share + (admin) everyone's marketing */}
        {((mine?.firms?.length || 0) > 0 || (isAdmin && (mktAll?.firms?.length || 0) > 0)) && (
          <div style={{ display: 'flex', gap: 6, marginBottom: 14, flexWrap: 'wrap' }}>
            {data && <TabBtn active={tab === 'firm'} onClick={() => setTab('firm')}>{t('fr_tab_firm')}</TabBtn>}
            {(mine?.firms || []).map((f: any) => (
              <TabBtn key={f.firm} active={tab === 'mine-' + f.firm} onClick={() => setTab('mine-' + f.firm)}>
                {t('fr_tab_mine')} · {f.firm === 'legal' ? 'Legal' : 'Accounting'}
              </TabBtn>
            ))}
            {isAdmin && (mktAll?.firms?.length || 0) > 0 && (
              <TabBtn active={tab === 'mkt-all'} onClick={() => setTab('mkt-all')}>{t('fr_tab_mkt_all')}</TabBtn>
            )}
          </div>
        )}

        {/* Controls */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16, flexWrap: 'wrap' }}>
          {tab === 'firm' && <>
          <label style={{ fontSize: 12.5, color: 'var(--text-secondary)', fontWeight: 700 }}>{t('fr_company')}</label>
          <select value={companyId} onChange={e => setCompanyId(e.target.value)} style={sel}>
            <option value="all">{t('fr_all')}</option>
            {(data?.companies || []).map(c => <option key={c.companyId} value={c.companyId}>{c.name}</option>)}
          </select>
          </>}
          <label style={{ fontSize: 12.5, color: 'var(--text-secondary)', fontWeight: 700, marginLeft: 6 }}>{t('fr_period')}</label>
          <select value={preset} onChange={e => onPreset(e.target.value)} style={sel}>
            <option value="this_month">{t('fr_this_month')}</option>
            <option value="last_month">{t('fr_last_month')}</option>
            <option value="this_year">{t('fr_this_year')}</option>
            <option value="custom">{t('fr_custom')}</option>
          </select>
          {preset === 'custom' && <>
            <input type="date" value={from} onChange={e => { setFrom(e.target.value); }} style={dateInp} />
            <span style={{ color: 'var(--text-tertiary)' }}>→</span>
            <input type="date" value={to} onChange={e => { setTo(e.target.value); }} style={dateInp} />
          </>}
          <span style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>{from} → {to}</span>
          {/* Without this the feature is unreachable: nothing else in the app ever classifies a
              payment, so every newly imported month would show its own costs as zero. */}
          <button onClick={classify} disabled={classifying} title="Work out what each payment out actually was — client money, cash, salary, rent, utilities, internet or other."
            style={{ marginLeft: 'auto', padding: '5px 12px', borderRadius: 8, border: '1px solid var(--border-strong)', background: 'var(--bg-surface)', color: 'var(--text-secondary)', fontSize: 12.5, fontWeight: 700, cursor: classifying ? 'not-allowed' : 'pointer', opacity: classifying ? 0.6 : 1 }}>
            <i className={classifying ? 'ti ti-loader-2' : 'ti ti-sparkles'} style={{ marginRight: 5, ...(classifying ? { display: 'inline-block', animation: 'spin 1s linear infinite' } : {}) }} />
            {classifying ? t('fr_classifying') : t('fr_classify')}
          </button>
        </div>

        {loading && <div style={{ color: 'var(--text-tertiary)', padding: 28, textAlign: 'center' }}>{t('fr_loading')}</div>}

        {!loading && tab.startsWith('mine-') && mine && (() => {
          const f = (mine.firms || []).find((x: any) => 'mine-' + x.firm === tab);
          return f ? <MineCard fig={f} t={t} /> : null;
        })()}

        {!loading && tab === 'firm' && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(340px, 1fr))', gap: 16 }}>
            {shown.map(c => <CompanyCard key={c.companyId} fig={c} t={t} />)}
            {companyId === 'all' && data && shown.length > 1 && <CompanyCard fig={{ companyId: 'total', name: t('fr_grand_total'), ...data.total }} t={t} highlight />}
          </div>
        )}

        {!loading && tab === 'mkt-all' && mktAll && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
            {(mktAll.firms || []).map((f: any) => <MarketingFirmTable key={f.firm} firm={f} t={t} />)}
          </div>
        )}

        <div style={{ fontSize: 11.5, color: 'var(--text-tertiary)', marginTop: 14, lineHeight: 1.6 }}>{tab === 'mkt-all' || tab.startsWith('mine-') ? t('fr_mine_note') : t('fr_note')}</div>
      </div>
    </div>
  );
}

function CompanyCard({ fig, t, highlight }: { fig: Fig; t: (k: string) => string; highlight?: boolean }) {
  const row = (label: string, val: number, opt: { bold?: boolean; color?: string; sub?: boolean; minus?: boolean; hint?: string } = {}) => (
    <div title={opt.hint} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', padding: opt.sub ? '3px 0 3px 14px' : '5px 0', fontSize: opt.sub ? 12.5 : 13.5 }}>
      <span style={{ color: opt.sub ? 'var(--text-tertiary)' : 'var(--text-secondary)', fontWeight: opt.bold ? 700 : 400 }}>{label}</span>
      <span style={{ color: opt.color || 'var(--text-primary)', fontWeight: opt.bold ? 800 : 600, fontVariantNumeric: 'tabular-nums' }}>{opt.minus ? '−' : ''}{baht(val)}</span>
    </div>
  );
  const sectionHdr = (s: string) => <div style={{ fontSize: 11, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '.03em', color: 'var(--text-tertiary)', margin: '14px 0 2px' }}>{s}</div>;

  return (
    <div style={{ background: 'var(--bg-surface)', border: highlight ? '2px solid var(--accent)' : '1px solid var(--border)', borderRadius: 12, padding: '14px 18px' }}>
      <div style={{ fontSize: 15, fontWeight: 800, color: 'var(--text-primary)', marginBottom: 4 }}>{fig.name}</div>

      {sectionHdr(t('fr_income'))}
      {row(t('fr_corporate'), fig.income.corporate)}
      {row(t('fr_personal'), fig.income.personal)}
      {row(t('fr_income_total'), fig.income.total, { bold: true })}
      {row(t('fr_passthrough'), fig.income.passThrough, { sub: true })}
      {row(t('fr_net'), fig.income.net, { bold: true, color: '#15803D' })}

      {sectionHdr(t('fr_expenses'))}
      {row(t('fr_expenses'), fig.expenses)}
      {fig.expensesSplit && (<>
        {row(t('fr_exp_clienttax'), fig.expensesSplit.clientTax, { sub: true,
          hint: 'Mirrors what was collected for clients on the income side, so the two net off against profit.' })}
        {/* What the classifier actually found going out for clients. Shown whenever it differs from
            the mirrored figure, so nobody has to wonder where the difference below comes from. */}
        {fig.expensesSplit.clientPaid !== undefined && Math.abs(fig.expensesSplit.clientPaid - fig.expensesSplit.clientTax) >= 0.01 && (
          <div style={{ display: 'flex', justifyContent: 'space-between', padding: '0 0 3px 28px', fontSize: 11.5, color: 'var(--text-tertiary)', fontStyle: 'italic' }}>
            <span>· {t('fr_exp_identified')}</span>
            <span style={{ fontVariantNumeric: 'tabular-nums' }}>{baht(fig.expensesSplit.clientPaid)}</span>
          </div>
        )}
        {row(t('fr_exp_cash'), fig.expensesSplit.cash, { sub: true })}
        {row(t('fr_exp_operating'), fig.expensesSplit.operating, { bold: true, color: '#B91C1C' })}
        {/* The firm's OWN running costs, split by what each payment actually was. Salary / rent /
            utilities / internet / other come from the classified payments; the last line closes the
            gap between them and the residual above — mostly the timing difference between taking a
            client's tax in and paying it out (owner 2026-08-21). */}
        {fig.expensesSplit.own && (<>
          {row(t('fr_exp_salary'), fig.expensesSplit.own.salary, { sub: true })}
          {row(t('fr_exp_rent'), fig.expensesSplit.own.rent, { sub: true })}
          {row(t('fr_exp_utilities'), fig.expensesSplit.own.utilities, { sub: true })}
          {row(t('fr_exp_internet'), fig.expensesSplit.own.internet, { sub: true })}
          {row(t('fr_exp_other'), fig.expensesSplit.own.other, { sub: true })}
          {/* NOT zero spend — payments with no verdict yet. Without this row a month nobody has
              classified reads as "the firm spent nothing on salary or rent" (review 2026-08-21). */}
          {!!fig.expensesSplit.unclassified && row(t('fr_exp_unclassified'), fig.expensesSplit.unclassified, { sub: true, color: '#B45309',
            hint: 'Payments out with no category yet — press "Classify expenses" above. The named costs exclude them; they are not zero spend, just unknown.' })}
          {!!fig.expensesSplit.unallocated && row(t('fr_exp_unalloc'), fig.expensesSplit.unallocated, { sub: true,
            hint: 'What went out for clients minus what came in for them. Positive = more paid out than collected this period (a tax taken in last month is remitted this one); negative = the reverse. It is here because the line above is pinned to the collected figure.' })}
        </>)}
      </>)}
      {/* The bottom line (owner 2026-08-21): total income less total expenses. The pass-through legs
          are equal by the owner's rule, so they cancel and this equals gross income less operating
          expenses too — the two ways of reading the card agree. */}
      {(() => { const net = fig.income.total - fig.expenses; return (
        <div style={{ borderTop: '1px solid var(--border)', marginTop: 6, paddingTop: 2 }}>
          {row(t('fr_net_final'), net, { bold: true, color: net < 0 ? '#B91C1C' : '#15803D', hint: t('fr_net_final_hint') })}
        </div>
      ); })()}

      {sectionHdr(t('fr_invoiced_quoted'))}
      {row(t('fr_invoiced_quoted'), fig.invoicedQuoted, { bold: true })}
      {row(t('fr_invoiced'), fig.invoiced, { sub: true })}
      {row(t('fr_quoted'), fig.quoted, { sub: true })}

      {sectionHdr(t('fr_receipted'))}
      {row(t('fr_receipted'), fig.receipted, { bold: true })}
    </div>
  );
}

function TabBtn({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button onClick={onClick} style={{ padding: '7px 16px', borderRadius: 8, border: 'none', fontWeight: 700, fontSize: 13, cursor: 'pointer', background: active ? 'var(--accent)' : 'var(--bg-surface)', color: active ? '#fff' : 'var(--text-secondary)', boxShadow: active ? 'none' : 'inset 0 0 0 1px var(--border)' }}>{children}</button>
  );
}

// The logged-in user's own marketing share for one firm (weighted invoiced / quoted / receipted).
function MineCard({ fig, t }: { fig: any; t: (k: string) => string }) {
  const firmName = fig.firm === 'legal' ? firmLabelOr('legal', t('cs_kind_legal')) : firmLabelOr('accounting', t('cs_kind_accounting'));
  const row = (label: string, val: number, opt: { bold?: boolean; sub?: boolean } = {}) => (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', padding: opt.sub ? '3px 0 3px 14px' : '5px 0', fontSize: opt.sub ? 12.5 : 13.5 }}>
      <span style={{ color: opt.sub ? 'var(--text-tertiary)' : 'var(--text-secondary)', fontWeight: opt.bold ? 700 : 400 }}>{label}</span>
      <span style={{ color: 'var(--text-primary)', fontWeight: opt.bold ? 800 : 600, fontVariantNumeric: 'tabular-nums' }}>{baht(val)}</span>
    </div>
  );
  const sectionHdr = (s: string) => <div style={{ fontSize: 11, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '.03em', color: 'var(--text-tertiary)', margin: '14px 0 2px' }}>{s}</div>;
  return (
    <div style={{ maxWidth: 480 }}>
      <div style={{ background: 'var(--bg-surface)', border: '1px solid var(--border)', borderRadius: 12, padding: '14px 18px' }}>
        <div style={{ fontSize: 15, fontWeight: 800, color: 'var(--text-primary)', marginBottom: 2 }}>{firmName} — {t('fr_mine_title')}</div>
        <div style={{ fontSize: 11.5, color: 'var(--text-tertiary)', marginBottom: 6 }}>{fig.isOffice ? t('fr_mine_office') : t('fr_mine_share')}</div>
        {sectionHdr(t('fr_invoiced_quoted'))}
        {row(t('fr_invoiced_quoted'), fig.invoicedQuoted, { bold: true })}
        {row(t('fr_invoiced'), fig.invoiced, { sub: true })}
        {row(t('fr_quoted'), fig.quoted, { sub: true })}
        {sectionHdr(t('fr_receipted'))}
        {row(t('fr_receipted'), fig.receipted, { bold: true })}
      </div>
      <div style={{ fontSize: 11.5, color: 'var(--text-tertiary)', marginTop: 10, lineHeight: 1.6 }}>{t('fr_mine_note')}</div>
    </div>
  );
}

// Admin: every marketing person's weighted figures for one firm, as a table (office = the remainder).
function MarketingFirmTable({ firm, t }: { firm: any; t: (k: string) => string }) {
  const firmName = firm.firm === 'legal' ? firmLabelOr('legal', t('cs_kind_legal')) : firmLabelOr('accounting', t('cs_kind_accounting'));
  const people: any[] = firm.people || [];
  const sum = (k: string) => people.reduce((a, p) => a + (Number(p[k]) || 0), 0);
  // 10.5 → 11.5px (Calem, report 66c9d174, 2nd opening: "Do the same for all other menus in the SLF system"). Then Active
  // Jobs' headings, as every staff list's (report 4bd4ae4e, 2nd opening: "Make all menus in the SLF system look like
  // this: the same height, text color, no bold, horizontal line colors etc."): black, --text-primary, at the regular
  // weight, where they were light grey in heavy bold (800), 10px above and below where it was 8, on one 16px line, on
  // white — a 37px band, as every list's is — over the darker --border-strong line. That line is the headings' own now;
  // it was the first row's lighter top line, which it outweighs where the two meet, the table collapsing its borders.
  const th: React.CSSProperties = { fontSize: 11.5, fontWeight: 400, textTransform: 'uppercase', letterSpacing: '.03em', color: 'var(--text-primary)', padding: '10px 12px', lineHeight: '16px', textAlign: 'right', whiteSpace: 'nowrap', borderBottom: '1px solid var(--border-strong)', background: 'var(--bg-surface)' };
  const td: React.CSSProperties = { fontSize: 13, color: 'var(--text-primary)', padding: '9px 12px', textAlign: 'right', fontVariantNumeric: 'tabular-nums', borderTop: '1px solid var(--border)' };
  return (
    <div>
      <div style={{ fontSize: 15, fontWeight: 800, color: 'var(--text-primary)', marginBottom: 8 }}>{firmName}</div>
      <div style={{ background: 'var(--bg-surface)', border: '1px solid var(--border)', borderRadius: 12, overflow: 'hidden' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead><tr>
            <th style={{ ...th, textAlign: 'left' }}>{t('fr_mkt_person')}</th>
            <th style={th}>{t('fr_invoiced_quoted')}</th>
            <th style={th}>{t('fr_invoiced')}</th>
            <th style={th}>{t('fr_quoted')}</th>
            <th style={th}>{t('fr_receipted')}</th>
          </tr></thead>
          <tbody>
            {people.map((p: any) => (
              <tr key={p.userId} style={p.isOffice ? { background: 'var(--c-gray-50)' } : undefined}>
                <td style={{ ...td, textAlign: 'left' }}>
                  <span style={{ fontWeight: 700 }}>{p.name}</span>
                  {p.isOffice && <span style={{ fontSize: 11, color: 'var(--text-tertiary)', marginLeft: 6 }}>({t('fr_mkt_remainder')})</span>}
                </td>
                <td style={{ ...td, fontWeight: 700 }}>{baht(p.invoicedQuoted)}</td>
                <td style={td}>{baht(p.invoiced)}</td>
                <td style={td}>{baht(p.quoted)}</td>
                <td style={td}>{baht(p.receipted)}</td>
              </tr>
            ))}
            {people.length > 1 && (
              <tr style={{ background: 'var(--bg-app)' }}>
                <td style={{ ...td, textAlign: 'left', fontWeight: 800 }}>{t('fr_grand_total')}</td>
                <td style={{ ...td, fontWeight: 800 }}>{baht(sum('invoicedQuoted'))}</td>
                <td style={{ ...td, fontWeight: 800 }}>{baht(sum('invoiced'))}</td>
                <td style={{ ...td, fontWeight: 800 }}>{baht(sum('quoted'))}</td>
                <td style={{ ...td, fontWeight: 800 }}>{baht(sum('receipted'))}</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
